/**
 * CharacterDataNormalizer - Ensures consistent character data structure
 * 
 * This utility normalizes character data from various sources (templates, custom characters)
 * into a unified structure that all components can rely on.
 */

export class CharacterDataNormalizer {
  /**
   * Normalize character data to consistent format
   * @param {Object} rawData - Raw character data from any source
   * @returns {Object} - Normalized character data
   */
  static normalize(rawData) {
    if (!rawData) {
      return this.getDefaultCharacter();
    }

    // Extract abilities from various possible formats
    const abilities = this.extractAbilities(rawData);
    
    // Get HP values with proper checks for all formats
    const hp = this.extractHP(rawData);
    const maxHp = this.extractMaxHP(rawData);
    
    // Log extracted HP and maxHP values for debugging
    console.log('[CharacterDataNormalizer] Extracted HP values:', { 
      hp, 
      maxHp, 
      originalHP: rawData.hp,
      originalMaxHP: rawData.maxHp 
    });
    
    // date_of_change: 2025-08-31 - Add debugging for field extraction
    console.log('[CharacterDataNormalizer] Raw data inspection:', {
      name: rawData.name,
      class: rawData.class,
      characterClass: rawData.characterClass,
      'data.class': rawData.data?.class,
      level: rawData.level,
      'data.level': rawData.data?.level,
      hasDataObject: !!rawData.data,
      dataKeys: rawData.data ? Object.keys(rawData.data) : 'no data object'
    });
    
    // Extract basic properties
    const normalized = {
      // Core identification  
      id: rawData.id || 'unknown',
      // date_of_change: 2025-08-31 - Enhanced name field detection
      name: rawData.name || rawData.characterName || rawData.data?.name || 
            rawData.data?.characterName || rawData.character?.name || 'Unknown Character',
      type: 'character',
      
      // date_of_change: 2025-08-31 - Enhanced class field detection for various file formats
      class: rawData.class || rawData.characterClass || rawData.data?.class || 
             rawData.classes?.[0]?.name || rawData.multiclass?.[0]?.name || 
             (rawData.name && rawData.name.toLowerCase().includes('wizard') ? 'Wizard' : 
              rawData.name && rawData.name.toLowerCase().includes('fighter') ? 'Fighter' :
              rawData.name && rawData.name.toLowerCase().includes('rogue') ? 'Rogue' :
              rawData.name && rawData.name.toLowerCase().includes('cleric') ? 'Cleric' : 'Fighter'),
      level: rawData.level || rawData.data?.level || rawData.characterLevel || 
             rawData.levels || rawData.totalLevel || 1,
      
      // Race, background, and alignment
      race: rawData.race || rawData.data?.race || 'Human',
      background: rawData.background || rawData.data?.background || 'Adventurer',
      alignment: rawData.alignment || rawData.data?.alignment || 'Neutral',
      
      // Combat stats
      hp: hp,
      maxHp: maxHp,
      // Also provide hp in object format for components that expect it
      hpObj: {
        current: hp,
        max: maxHp
      },
      ac: this.extractAC(rawData),
      initiative: this.calculateInitiative(rawData, abilities),
      proficiencyBonus: rawData.proficiencyBonus || rawData.data?.proficiencyBonus || 2,
      speed: rawData.speed || rawData.data?.speed || 30,
      
      // Ability scores (unified format - use abilities only)
      abilities: {
        strength: abilities.str,
        dexterity: abilities.dex,
        constitution: abilities.con,
        intelligence: abilities.int,
        wisdom: abilities.wis,
        charisma: abilities.cha
      },
      
      // Also provide abilities with short names for backward compatibility
      abilitiesShort: {
        str: abilities.str,
        dex: abilities.dex,
        con: abilities.con,
        int: abilities.int,
        wis: abilities.wis,
        cha: abilities.cha
      },
      
      // Combat modifiers
      skills: rawData.skills || rawData.data?.skills || {},
      savingThrows: rawData.savingThrows || rawData.data?.savingThrows || {},
      
      // Actions and spellcasting
      actions: rawData.actions || rawData.data?.actions || [],
      features: rawData.features || rawData.data?.features || [],
      // date_of_change: 2025-09-01 - FIXED: Properly handle UploadDataNormalizer equipment structure
      equipment: this.extractEquipment(rawData),
      spells: rawData.spells || rawData.data?.spells || [],
      spellcasting: rawData.spellcasting || rawData.data?.spellcasting || null,
      
      // Artwork
      artwork: rawData.artwork || rawData.data?.artwork || '/assets/characters/default.jpg',
      
      // date_of_change: 2025-09-01 - Added biography and personality fields
      // Experience Points
      experiencePoints: rawData.experiencePoints || rawData.data?.experiencePoints || 
                       rawData.xp || rawData.data?.xp || 0,
      
      // Character Biography
      appearance: rawData.appearance || rawData.data?.appearance || 
                 rawData.description || rawData.data?.description || '',
      backstory: rawData.backstory || rawData.data?.backstory || 
                rawData.biography || rawData.data?.biography || 
                rawData.history || rawData.data?.history || '',
      
      // Personality Traits
      personalityTraits: rawData.personalityTraits || rawData.data?.personalityTraits || 
                        rawData.personalTraits || rawData.data?.personalTraits || '',
      ideals: rawData.ideals || rawData.data?.ideals || '',
      bonds: rawData.bonds || rawData.data?.bonds || '',
      flaws: rawData.flaws || rawData.data?.flaws || '',
      
      // Metadata
      isCustom: rawData.isCustom || false,
      createdAt: rawData.createdAt || null
    };

    // Process spellcasting data if present
    if (normalized.spellcasting) {
      normalized.spellcasting = this.normalizeSpellcasting(normalized.spellcasting, abilities);
    }
    
    // Debug log before validation
    console.log('[CharacterDataNormalizer] Normalized data:', {
      name: normalized.name,
      abilities: normalized.abilities,
      abilitiesShort: normalized.abilitiesShort,
      hp: normalized.hp,
      maxHp: normalized.maxHp,
      hpObj: normalized.hpObj
    });

    // Validate the normalized data
    this.validate(normalized);
    
    return normalized;
  }

  /**
   * Extract ability scores from various formats
   */
  static extractAbilities(data) {
    // Try stats format first (preferred)
    if (data.stats && typeof data.stats === 'object' && data.stats.str) {
      return {
        str: data.stats.str || 10,
        dex: data.stats.dex || 10,
        con: data.stats.con || 10,
        int: data.stats.int || 10,
        wis: data.stats.wis || 10,
        cha: data.stats.cha || 10
      };
    }
    
    // Try abilities format with short names
    if (data.abilities && typeof data.abilities === 'object' && data.abilities.str) {
      return {
        str: data.abilities.str || 10,
        dex: data.abilities.dex || 10,
        con: data.abilities.con || 10,
        int: data.abilities.int || 10,
        wis: data.abilities.wis || 10,
        cha: data.abilities.cha || 10
      };
    }
    
    // Try abilities format with long names
    if (data.abilities && typeof data.abilities === 'object' && data.abilities.strength) {
      return {
        str: data.abilities.strength || 10,
        dex: data.abilities.dexterity || 10,
        con: data.abilities.constitution || 10,
        int: data.abilities.intelligence || 10,
        wis: data.abilities.wisdom || 10,
        cha: data.abilities.charisma || 10
      };
    }
    
    // Try nested data.abilities format
    if (data.data?.abilities && typeof data.data.abilities === 'object') {
      if (data.data.abilities.str) {
        return {
          str: data.data.abilities.str || 10,
          dex: data.data.abilities.dex || 10,
          con: data.data.abilities.con || 10,
          int: data.data.abilities.int || 10,
          wis: data.data.abilities.wis || 10,
          cha: data.data.abilities.cha || 10
        };
      } else if (data.data.abilities.strength) {
        return {
          str: data.data.abilities.strength || 10,
          dex: data.data.abilities.dexterity || 10,
          con: data.data.abilities.constitution || 10,
          int: data.data.abilities.intelligence || 10,
          wis: data.data.abilities.wisdom || 10,
          cha: data.data.abilities.charisma || 10
        };
      }
    }
    
    // Try direct properties
    if (data.strength || data.str || data.dexterity || data.dex) {
      return {
        str: data.strength || data.str || 10,
        dex: data.dexterity || data.dex || 10,
        con: data.constitution || data.con || 10,
        int: data.intelligence || data.int || 10,
        wis: data.wisdom || data.wis || 10,
        cha: data.charisma || data.cha || 10
      };
    }
    
    // Default stats
    return { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 };
  }

  /**
   * Extract Hit Points from various formats
   */
  static extractHP(data) {
    if (typeof data.hp === 'number') return data.hp;
    if (data.hp?.current) return data.hp.current;
    if (data.hp?.value) return data.hp.value; // Added support for value property
    if (data.hitPoints) return data.hitPoints;
    if (data.data?.hp?.current) return data.data.hp.current; // Nested current
    if (data.data?.hp?.value) return data.data.hp.value; // Nested value
    if (data.data?.hp) return data.data.hp;
    if (data.data?.hitPoints) return data.data.hitPoints;
    return 10;
  }

  /**
   * Extract Max Hit Points
   */
  static extractMaxHP(data) {
    if (typeof data.maxHp === 'number') return data.maxHp;
    if (data.hp?.max) return data.hp.max;
    if (data.hp?.maximum) return data.hp.maximum; // Added support for maximum property
    if (data.maxHitPoints) return data.maxHitPoints;
    if (data.data?.hp?.max) return data.data.hp.max; // Nested max
    if (data.data?.hp?.maximum) return data.data.hp.maximum; // Nested maximum
    if (data.data?.maxHp) return data.data.maxHp;
    if (data.data?.maxHitPoints) return data.data.maxHitPoints;
    
    // Fallback to current HP
    const hp = this.extractHP(data);
    return data.maxHp || hp;
  }

  /**
   * Extract Armor Class from various formats
   */
  static extractAC(data) {
    if (typeof data.ac === 'number') return data.ac;
    if (data.armorClass) return data.armorClass;
    if (data.data?.ac) return data.data.ac;
    if (data.data?.armorClass) return data.data.armorClass;
    return 10;
  }

  /**
   * Calculate initiative bonus
   */
  static calculateInitiative(data, abilities) {
    if (typeof data.initiative === 'number') return data.initiative;
    if (data.data?.initiative) return data.data.initiative;
    
    // Default to DEX modifier
    return Math.floor((abilities.dex - 10) / 2);
  }

  /**
   * Normalize spellcasting data
   * @date_of_change 2025-09-06 - CRITICAL FIX: Preserve EntityStorage prepared spells array
   */
  static normalizeSpellcasting(spellcasting, abilities) {
    if (!spellcasting) return null;
    
    // @date_of_change 2025-09-06 - DEBUG: Log input spellcasting to verify prepared spells
    console.log('[CharacterDataNormalizer] normalizeSpellcasting input:', {
      hasSpellcasting: !!spellcasting,
      keys: Object.keys(spellcasting),
      hasPrepared: !!spellcasting.prepared,
      preparedSpells: spellcasting.prepared
    });
    
    const normalized = {
      spellcastingAbility: spellcasting.spellcastingAbility || spellcasting.ability || 'int',
      spellAttackBonus: spellcasting.spellAttackBonus || 0,
      spellSaveDC: spellcasting.spellSaveDC || 10,
      spellSlots: spellcasting.spellSlots || spellcasting.slots || {},
      spellsKnown: spellcasting.spellsKnown || [],
      // CRITICAL: Preserve EntityStorage prepared spells array
      prepared: spellcasting.prepared || [],
      // Preserve other EntityStorage spellcasting properties
      concentration: spellcasting.concentration || null,
      spellList: spellcasting.spellList || []
    };
    
    // @date_of_change 2025-09-06 - DEBUG: Log output to verify prepared spells are preserved
    console.log('[CharacterDataNormalizer] normalizeSpellcasting output:', {
      keys: Object.keys(normalized),
      hasPrepared: !!normalized.prepared,
      preparedSpells: normalized.prepared
    });
    
    // Calculate spell attack bonus if not provided
    if (!spellcasting.spellAttackBonus) {
      const abilityScore = normalized.spellcastingAbility === 'int' ? abilities.int :
                          normalized.spellcastingAbility === 'wis' ? abilities.wis :
                          normalized.spellcastingAbility === 'cha' ? abilities.cha : abilities.int;
      const abilityMod = Math.floor((abilityScore - 10) / 2);
      normalized.spellAttackBonus = abilityMod + 2; // Assume proficiency bonus of 2
    }
    
    // Calculate spell save DC if not provided
    if (!spellcasting.spellSaveDC) {
      normalized.spellSaveDC = 8 + normalized.spellAttackBonus;
    }
    
    return normalized;
  }

  /**
   * Extract and normalize equipment from various formats
   * date_of_change: 2025-09-01 - Handle UploadDataNormalizer comprehensive equipment
   */
  static extractEquipment(rawData) {
    // Try to get equipment from various locations
    const equipmentData = rawData.equipment || rawData.data?.equipment || {};
    
    // If no equipment data found, return empty structure
    if (!equipmentData || typeof equipmentData !== 'object') {
      return {
        weapons: [],
        armor: [],
        items: []
      };
    }
    
    // If equipment is already in the expected format (has weapons, armor, items arrays), use it
    if (Array.isArray(equipmentData.weapons) && Array.isArray(equipmentData.armor) && Array.isArray(equipmentData.items)) {
      console.log('[CharacterDataNormalizer] Using comprehensive equipment format');
      return {
        weapons: equipmentData.weapons || [],
        armor: equipmentData.armor || [],
        items: equipmentData.items || [],
        currency: equipmentData.currency || {}
      };
    }
    
    // Handle legacy format where equipment might be a flat array
    if (Array.isArray(equipmentData)) {
      console.log('[CharacterDataNormalizer] Converting legacy equipment array format');
      return {
        weapons: equipmentData.filter(item => item.damage || item.attackBonus),
        armor: equipmentData.filter(item => item.ac || item.armorClass),
        items: equipmentData.filter(item => !item.damage && !item.attackBonus && !item.ac && !item.armorClass)
      };
    }
    
    // Handle other flat formats
    console.log('[CharacterDataNormalizer] Converting flat equipment format');
    return {
      weapons: [],
      armor: [], 
      items: []
    };
  }
  
  /**
   * Get a default character data structure
   * @returns {Object} Default character data
   */
  static getDefaultCharacter() {
    return {
      id: 'unknown',
      name: 'Unknown Character',
      type: 'character',
      class: 'Fighter',
      level: 1,
      race: 'Human',
      background: 'Adventurer',
      alignment: 'Neutral',
      hp: 10,
      maxHp: 10,
      ac: 10,
      initiative: 0,
      proficiencyBonus: 2,
      speed: 30,
      // Only include abilities in the long form for consistency
      abilities: {
        strength: 10,
        dexterity: 10,
        constitution: 10,
        intelligence: 10,
        wisdom: 10,
        charisma: 10
      },
      skills: {},
      savingThrows: {},
      actions: [],
      features: [],
      equipment: {
        weapons: [],
        armor: [],
        items: []
      },
      spells: [],
      spellcasting: null,
      artwork: '/assets/characters/default.jpg',
      // date_of_change: 2025-09-01 - Added biography and personality fields to default character
      experiencePoints: 0,
      appearance: '',
      backstory: '',
      personalityTraits: '',
      ideals: '',
      bonds: '',
      flaws: '',
      isCustom: false,
      createdAt: null
    };
  }

  /**
   * Basic validation of character data
   * @param {Object} character - Character data to validate
   */
  static validate(character) {
    // Check for required fields
    const requiredFields = ['name', 'class', 'level', 'hp', 'maxHp', 'ac'];
    for (const field of requiredFields) {
      if (character[field] === undefined || character[field] === null) {
        console.warn(`[CharacterDataNormalizer] Character missing required field: ${field}`, character);
      }
    }
    
    // Check for valid ability scores
    // Map from long to short form for validation
    const abilityMap = {
      'strength': 'str',
      'dexterity': 'dex',
      'constitution': 'con',
      'intelligence': 'int',
      'wisdom': 'wis',
      'charisma': 'cha'
    };
    
    // First check abilities object (preferred format)
    if (character.abilities) {
      for (const ability of Object.keys(abilityMap)) {
        const score = character.abilities[ability];
        if (!score || score < 1 || score > 30) {
          console.warn(`[CharacterDataNormalizer] Character has invalid ${ability} score: ${score}`, character);
        }
      }
    }
    // Fall back to stats object if abilities are missing
    else if (character.stats) {
      for (const ability of Object.values(abilityMap)) {
        const score = character.stats[ability];
        if (!score || score < 1 || score > 30) {
          console.warn(`[CharacterDataNormalizer] Character has invalid ${ability} score: ${score}`, character);
        }
      }
    }
    else {
      console.warn('[CharacterDataNormalizer] Character missing ability scores entirely', character);
    }
    
    // Check for valid level
    if (character.level < 1 || character.level > 20) {
      console.warn(`[CharacterDataNormalizer] Character has invalid level: ${character.level}`, character);
    }
    
    // Check for valid HP
    if (character.hp < 0) {
      console.warn(`[CharacterDataNormalizer] Character has negative HP: ${character.hp}`, character);
    }
    
    if (character.maxHp < 1) {
      console.warn(`[CharacterDataNormalizer] Character has invalid maxHp: ${character.maxHp}`, character);
    }
    
    // Check for valid AC
    if (character.ac < 1) {
      console.warn(`[CharacterDataNormalizer] Character has invalid AC: ${character.ac}`, character);
    }
  }
}

export default CharacterDataNormalizer;
